import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import HeatmapLayer from 'ol/layer/Heatmap';
import Cluster from 'ol/source/Cluster';
import VectorSource from 'ol/source/Vector';
import { useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAllowHeatmapRender,
  selectAnnotationOpacity,
  selectAnnotationReviewFilters,
  selectClassifications,
  selectDisplayDiffType,
  selectIgnoredCellTypes,
  setAllowHeatmapRender,
} from 'redux/slices/analysis';
import { selectShouldShowHeatmapLegend, setShouldShowHeatmapLegend } from 'redux/slices/userInterface';
import { selectIsHeatmapEnabled, setIsAnnotatingEnabled, setIsAnnotatingPrevented } from 'redux/slices/viewer';
import { ANNOTATION_LAYER_MIN_ZOOM, HEATMAP_LAYER_MAX_ZOOM, OL_LAYER_NAME } from 'utils/constants';
import ViewerContext from 'components/Viewer/ViewerContext';
import {store} from "../../redux/store";

export default function AnnotationLayerHeatmap({ source }: HeatmapLayerProps) {
  const { map } = useContext(ViewerContext);
  const dispatch = useDispatch();
  const classes = useSelector(selectClassifications);
  const allowHeatmapRender = useSelector(selectAllowHeatmapRender);
  const heatmapLayer = useRef<HeatmapLayer | null>(null);
  const ignoredCellTypes = useSelector(selectIgnoredCellTypes);
  const annotationReviewFilters = useSelector(selectAnnotationReviewFilters);
  const displayDiffType = useSelector(selectDisplayDiffType);
  const annotationOpacity = useSelector(selectAnnotationOpacity);
  const shouldShowHeatmapLegend = useSelector(selectShouldShowHeatmapLegend);
  const isHeatmapEnabled = useSelector(selectIsHeatmapEnabled);

  useEffect(() => {
    if (!heatmapLayer.current) return;
    heatmapLayer.current?.setOpacity((annotationOpacity / 10) * 6);
    heatmapLayer.current?.changed();
  }, [annotationOpacity]);

  useEffect(() => {
    dispatch(setAllowHeatmapRender(isHeatmapEnabled));
    dispatch(setShouldShowHeatmapLegend(isHeatmapEnabled));
  }, [dispatch, isHeatmapEnabled]);

  // Only add/render HeatmapLayer when all annotations have been loaded to source, otherwise there is a huge performance
  // hit if annotations are added to source when Heatmap is already added
  // (e.g. UserChanges added after initial WSI Results load)
  // This useEffect also tracks shouldShowHeatmapLegend so that we can include newly drawn/changed annotations in the
  // featureList if the User zooms out and the Heatmap is visible again
  useEffect(() => {
    if (!map || !source || classes.length === 0 || !allowHeatmapRender || !shouldShowHeatmapLegend) return;

    if (displayDiffType === 'wsi_results_only') return;

    // Clone list of features from source so that features are not connected and changes to AnnotationLayer features
    // don't cause re-renders in AnnotationLayerHeatmap even if not visible
    let featuresToCopy;
    if (ignoredCellTypes.length > 0) {
      featuresToCopy = source
        .getFeatures()
        .filter((feat: Feature<Point>) => !ignoredCellTypes.includes(feat.get('type')));
    } else {
      featuresToCopy = source.getFeatures();
    }

    if (annotationReviewFilters.length > 0) {
      const showReviewedCells = annotationReviewFilters.includes('show_reviewed');
      const showUnReviewedCells = annotationReviewFilters.includes('show_unreviewed');

      featuresToCopy = featuresToCopy.filter((feat: Feature<Point>) => {
        if (feat.get('is_reviewed')) {
          return showReviewedCells;
        }
        return showUnReviewedCells;
      });
    } else {
      featuresToCopy = [];
    }

    const featureList = featuresToCopy.map((feat: Feature<Point>) => feat.clone());

    const sourceToUse = new VectorSource({ features: featureList });

    const clusterSource: any = new Cluster({
      distance: 10,
      source: sourceToUse,
      wrapX: false,
    });

    heatmapLayer.current = new HeatmapLayer({
      className: OL_LAYER_NAME.HEATMAP,
      source: clusterSource,
      zIndex: 1,
      maxZoom: HEATMAP_LAYER_MAX_ZOOM,
      blur: 20,
      radius: 10,
      properties: { name: OL_LAYER_NAME.HEATMAP },
      opacity: 0.6,
    });

    map.addLayer(heatmapLayer.current);

    map.getView().on('change:resolution', event => {
      if (!heatmapLayer.current) return;

      const state = store.getState();
      const isHeatmapEnabled = selectIsHeatmapEnabled(state);
      if (!isHeatmapEnabled) {
        return;
      }

      // 'Fade out' Layer as user zooms in
      const newZoom = event.target.getZoom();
      if (newZoom < HEATMAP_LAYER_MAX_ZOOM && newZoom > ANNOTATION_LAYER_MIN_ZOOM) {
        const newOpacity = -(newZoom - HEATMAP_LAYER_MAX_ZOOM);
        heatmapLayer.current.setOpacity(newOpacity);
      }

      if (newZoom >= HEATMAP_LAYER_MAX_ZOOM) {
        dispatch(setIsAnnotatingPrevented(false));
        dispatch(setShouldShowHeatmapLegend(false));
      } else {
        dispatch(setIsAnnotatingPrevented(true));
        dispatch(setIsAnnotatingEnabled(false));
        dispatch(setShouldShowHeatmapLegend(true));
      }
    });

    return () => {
      if (map && heatmapLayer.current) {
        clusterSource.setSource(null);
        map.removeLayer(heatmapLayer.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    map,
    source,
    classes,
    allowHeatmapRender,
    ignoredCellTypes,
    annotationReviewFilters,
    displayDiffType,
    shouldShowHeatmapLegend,
  ]);

  return null;
}
